/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { MoreOutlined } from '@ant-design/icons';

import { NodeResponse } from '@/client';
import { GuardedButton } from '@/components/GuardedButton';
import { EditableNodeName } from '@/components/Node/EditableNodeName';
import { PageHeader } from '@/components/PageHeader';
import { TagsField } from '@/components/TagsField';
import { useMessage } from '@/hooks/message';
import { useAddTagMutation } from '@/hooks/mutation/useAddTagMutation';
import { useArchiveNodeMutation } from '@/hooks/mutation/useArchiveNodeMutation';
import { useNewTagMutation } from '@/hooks/mutation/useNewTagMutation';
import { useRemoveTagMutation } from '@/hooks/mutation/useRemoveTagMutation';
import { useUpdateBaselineMutation } from '@/hooks/mutation/useUpdateBaselineMutation';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { NodeSubtenantSelect } from '@/pages/NodeDetails/NodeSubtenantSelect';
import { Button } from '@/primitives/Button';
import { DomainControllerBadge } from '@/primitives/DomainControllerBadge';
import { Dropdown, MenuProps } from '@/primitives/Dropdown';
import { Flex } from '@/primitives/Flex';
import { NodeIcon } from '@/primitives/Icons';
import { Tag } from '@/primitives/Tag';
import { isDomainController } from '@/utils/isDomainController';
import './NodeDetailsHeader.css';

export type NodeDetailsHeaderProps = {
  nodeData: NodeResponse;
  onNameChange: (newNodeName: string) => void;
  nodeTags: string[];
  tenantID: string;
  nodeId: string;
  maintenanceModeButton?: React.ReactNode;
};

export function NodeDetailsHeader({
  nodeData,
  onNameChange,
  nodeTags,
  tenantID,
  nodeId,
  maintenanceModeButton,
}: NodeDetailsHeaderProps) {
  const [message] = useMessage();
  const { t } = useMimicTranslation('node');
  const { t: tTags } = useMimicTranslation('tags');

  const isAdmin = useAuthorization('admin');

  const isArchived = !!nodeData.data.dateArchived;

  const { mutate: archiveNode, isPending: archivePending } = useArchiveNodeMutation({
    onSuccess: () => {
      message.success(t('archiveNodeSuccess'));
    },
    onError: (error) => {
      const apiError = error as { status?: number };
      if (apiError.status === 400) {
        message.error(t('archiveNodeMustBeDisconnected'));
      } else {
        message.error(t('archiveNodeError'));
      }
    },
  });

  const nodeActionsMenuItems: MenuProps['items'] = [
    {
      label: t('archive_node'),
      key: 'archive-node',
      disabled: isArchived,
      title: isArchived ? t('archiveNodeDisabled') : undefined,
    },
  ];

  const nodeActionsMenu: MenuProps = {
    items: nodeActionsMenuItems,
    onClick: (e) => {
      if (e.key === 'archive-node') {
        archiveNode({ nodeId });
      }
    },
  };

  const { mutateAsync: updateBaseline, isPending: updateBaselinePending } = useUpdateBaselineMutation(
    tenantID!,
    nodeData.data,
    {
      onError: () => {
        message.error(tTags('updateBaselineError'));
      },
      onSuccess: () => {
        message.success(tTags('updateBaselineSuccess'));
      },
    },
  );

  const { mutateAsync: addTag } = useAddTagMutation({
    onError: (_error, tag) => {
      message.error(tTags('addTagError', { name: tag.tagName }));
    },
  });
  const { mutate: removeTag } = useRemoveTagMutation({
    onError: (_error, tag) => {
      message.error(tTags('removeTagError', { name: tag.tagName }));
    },
  });
  const { mutateAsync: newTagMutate } = useNewTagMutation({
    onError: (_error, tag) => {
      message.error(tTags('newTagCreatedError', { name: tag.name }));
    },
  });

  const onAddTag = async (tagName: string) => {
    await addTag({ nodeId, tagName });
  };

  const onRemoveTag = (tagName: string) => {
    removeTag({ nodeId, tagName });
  };

  const onCreateTag = async (name: string) => {
    await newTagMutate({ name });
  };

  return (
    <div style={{ width: '100%' }}>
      <Flex gap="xs" align="center">
        <PageHeader
          icon={<NodeIcon />}
          title={
            <Flex gap="xs" align="center">
              <EditableNodeName name={nodeData.data.name} onNameChange={onNameChange} />
              {nodeData.data.dateArchived && <Tag>{t('archived')}</Tag>}
              {isDomainController(nodeData.data) && <DomainControllerBadge />}
            </Flex>
          }
          level={4}
          fontWeight="300"
        />
      </Flex>

      <div className="tag-action-container">
        <Flex align="center" gap="sm" wrap>
          <NodeSubtenantSelect node={nodeData.data} />
          <TagsField selectedTags={nodeTags} onAddTag={onAddTag} onRemoveTag={onRemoveTag} onCreateTag={onCreateTag} />
        </Flex>
        <div className="node-details-header-actions">
          {maintenanceModeButton}
          {!isArchived && (
            <GuardedButton
              requiredRole="admin"
              type="primary"
              dataTestId="update-baseline"
              loading={updateBaselinePending}
              onClick={() => updateBaseline()}
            >
              {t('update_baseline')}
            </GuardedButton>
          )}
          {isAdmin && (
            <Dropdown menu={nodeActionsMenu} dataTestId="node-actions-dropdown">
              <Button type="default" dataTestId="node-actions-button" loading={archivePending}>
                <MoreOutlined />
              </Button>
            </Dropdown>
          )}
        </div>
      </div>
    </div>
  );
}
